import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getDetailedErrorMessage } from '@/lib/errorParser';
import { useErrorReporting } from './useErrorReporting';

export interface SharePointFile {
  id: string;
  name: string;
  size: number;
  createdDateTime: string;
  lastModifiedDateTime: string;
  webUrl: string;
  downloadUrl?: string;
  folder: boolean;
  mimeType?: string;
}

export const useSharePointFiles = (projectId: string) => {
  const [files, setFiles] = useState<SharePointFile[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { reportError } = useErrorReporting();

  const fetchFiles = async (path = '/') => {
    setLoading(true);
    // Normalize path before sending
    const normalizedPath = (path || '').replace(/^\/+/, '');
    
    try {
      const { data, error } = await supabase.functions.invoke('sharepoint-files', {
        body: { 
          action: 'list',
          projectId, 
          path: normalizedPath
        }
      });

      if (error) throw error;
      
      if (data.error) {
        // Propagate structured errors from edge function
        const err = new Error(data.message || 'SharePoint error');
        (err as any).code = data.error;
        (err as any).status = data.error === 'not_found' ? 404 : 
                             data.error === 'unauthorized' ? 401 :
                             data.error === 'forbidden' ? 403 : 500;
        throw err;
      }
      
      setFiles(data.files || []);
    } catch (error: any) {
      console.error('Error fetching SharePoint files:', error);
      
      await reportError(
        'SharePoint files fetch failed',
        error,
        {
          action: 'fetch_sharepoint_files',
          projectId,
          path
        }
      );
      
      // Re-throw to allow caller to handle
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const uploadSingleFile = async (file: File, normalizedPath: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    formData.append('path', normalizedPath);

    const { data, error } = await supabase.functions.invoke('sharepoint-files', {
      body: formData
    });

    // Check supabase invoke error
    if (error) throw error;
    
    // Check response body error (edge function returned error details)
    if (data?.error) {
      const err = new Error(data.message || 'SharePoint upload failed');
      (err as any).code = data.error;
      (err as any).status = data.status;
      (err as any).details = data.details;
      throw err;
    }
    
    return file;
  };

  const uploadMultipleFiles = async (
    files: File[], 
    path: string, 
    onProgress?: (completedCount: number) => void
  ) => {
    setLoading(true);
    const normalizedPath = (path || '').replace(/^\/+/, '');
    
    let successCount = 0;
    let failCount = 0;
    const failedFiles: string[] = [];
    
    try {
      // Upload alle bestanden parallel
      const uploadPromises = files.map(file => 
        uploadSingleFile(file, normalizedPath)
          .then(() => {
            successCount++;
            onProgress?.(successCount + failCount);
            return { status: 'success', file: file.name };
          })
          .catch((error) => {
            failCount++;
            failedFiles.push(file.name);
            onProgress?.(successCount + failCount);
            console.error(`Failed to upload ${file.name}:`, {
              fileName: file.name,
              fileSize: file.size,
              error: error.message,
              code: error.code,
              status: error.status,
              details: error.details
            });
            return { status: 'error', file: file.name, error };
          })
      );
      
      await Promise.allSettled(uploadPromises);
      
      // Toon samenvatting toast
      if (successCount > 0 && failCount === 0) {
        toast({
          title: 'Upload succesvol',
          description: `${successCount} ${successCount === 1 ? 'bestand' : 'bestanden'} geüpload naar SharePoint`
        });
      } else if (successCount > 0 && failCount > 0) {
        toast({
          title: 'Upload gedeeltelijk succesvol',
          description: `${successCount} bestanden geüpload, ${failCount} mislukt: ${failedFiles.join(', ')}`,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Upload mislukt',
          description: `Alle ${failCount} bestanden zijn mislukt`,
          variant: 'destructive'
        });
      }
      
      await fetchFiles(path);
    } catch (error: any) {
      console.error('Error in batch upload:', error);
      
      await reportError(
        'SharePoint batch upload failed',
        error,
        {
          action: 'upload_multiple_sharepoint_files',
          projectId,
          fileCount: files.length,
          path
        }
      );
      
      toast({
        title: 'Upload mislukt',
        description: 'Er ging iets mis bij het uploaden van de bestanden',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (itemId: string, currentPath: string) => {
    try {
      const { error } = await supabase.functions.invoke('sharepoint-files', {
        body: { 
          action: 'delete',
          itemId, 
          projectId 
        }
      });

      if (error) throw error;

      toast({
        title: 'Bestand verwijderd',
        description: 'Het bestand is succesvol verwijderd'
      });

      await fetchFiles(currentPath);
    } catch (error: any) {
      console.error('Error deleting file:', error);
      
      const detailedMessage = getDetailedErrorMessage(error);
      
      toast({
        title: 'SharePoint verwijderen mislukt',
        description: detailedMessage,
        variant: 'destructive'
      });
      
      await reportError(
        'SharePoint file delete failed',
        error,
        {
          action: 'delete_sharepoint_file',
          projectId,
          itemId
        }
      );
    }
  };

  const moveFiles = async (fileIds: string[], targetPath: string, currentPath: string) => {
    setLoading(true);
    const normalizedTarget = (targetPath || '').replace(/^\/+/, '');
    
    let successCount = 0;
    let failCount = 0;
    
    try {
      const movePromises = fileIds.map(async (itemId) => {
        try {
          const { error } = await supabase.functions.invoke('sharepoint-files', {
            body: { 
              action: 'move',
              itemId,
              targetPath: normalizedTarget,
              projectId 
            }
          });

          if (error) throw error;
          successCount++;
          return { status: 'success', itemId };
        } catch (error) {
          failCount++;
          console.error(`Failed to move file ${itemId}:`, error);
          return { status: 'error', itemId, error };
        }
      });

      await Promise.allSettled(movePromises);

      if (successCount > 0 && failCount === 0) {
        toast({
          title: 'Bestanden verplaatst',
          description: `${successCount} ${successCount === 1 ? 'bestand' : 'bestanden'} succesvol verplaatst`
        });
      } else if (successCount > 0 && failCount > 0) {
        toast({
          title: 'Gedeeltelijk verplaatst',
          description: `${successCount} bestanden verplaatst, ${failCount} mislukt`,
          variant: 'default'
        });
      } else {
        toast({
          title: 'Verplaatsen mislukt',
          description: `Alle ${failCount} bestanden zijn mislukt`,
          variant: 'destructive'
        });
      }

      await fetchFiles(currentPath);
    } catch (error: any) {
      console.error('Error in batch move:', error);
      
      await reportError(
        'SharePoint batch move failed',
        error,
        {
          action: 'move_multiple_sharepoint_files',
          projectId,
          fileCount: fileIds.length,
          targetPath
        }
      );
      
      toast({
        title: 'Verplaatsen mislukt',
        description: 'Er ging iets mis bij het verplaatsen van de bestanden',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getPreview = async (itemId: string) => {
    try {
      const SHAREPOINT_SITE_ID = 'YOUR_SITE_ID';
      const SHAREPOINT_DRIVE_ID = 'YOUR_DRIVE_ID';
      
      const { data: { session } } = await supabase.auth.getSession();
      const { data: settings } = await supabase
        .from('user_settings')
        .select('microsoft_access_token')
        .eq('user_id', session?.user?.id)
        .single();

      const response = await fetch(
        `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_SITE_ID}/drives/${SHAREPOINT_DRIVE_ID}/items/${itemId}/preview`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${settings?.microsoft_access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      return data.getUrl;
    } catch (error) {
      console.error('Error getting preview:', error);
      return null;
    }
  };

  return {
    files,
    loading,
    fetchFiles,
    uploadMultipleFiles,
    deleteFile,
    moveFiles,
    getPreview
  };
};